<template>
  <div class="flex flex-col gap-1.5">
    <ui-button
      :variant="active ? 'destructive' : 'default'"
      class="w-full"
      @click="() => onClickButton()"
    >
      <trash-2-icon v-if="active" />
      <upload-icon v-else />
      {{ buttonLabel }}
    </ui-button>
    <input
      ref="fileInput"
      type="file"
      data-slot="file-input"
      class="hidden"
      :accept="accept"
      @change="onFileChange"
    >

    <p class="m-0 text-center text-xs text-muted-foreground">
      <template v-if="uploadedFileName">
        {{ tr('Uploaded: :fileName', { ':fileName': uploadedFileName }) }}
      </template>
      <template v-else>
        {{ tr('Maximum upload size :limit', { ':limit': readableLimit }) }}
        ({{ readableFileTypes }})
      </template>
    </p>
  </div>
</template>

<script>
import { Trash2, Upload } from '@lucide/vue';

/**
 * Single image file picker button (v-model: File | null). `imagePreview` holds
 * an object URL of the chosen file for callers that render it themselves.
 */
export default {
  name: 'ImageInput',
  components: {
    Trash2Icon: Trash2,
    UploadIcon: Upload,
  },
  props: {
    modelValue: {
      type: [ File, null ],
      required: false,
      default: () => null,
    },
    label: {
      type: String,
      required: true,
    },
    activeLabel: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    sizeLimit: {
      type: Number,
      required: false,
      default: 5 * 1024 * 1024, // 5 MB
    },
    accept: {
      type: String,
      required: false,
      default: 'image/jpeg, image/png, image/webp',
    },
  },
  data: () => ({
    imagePreview: null,
    uploadedFileName: null,
  }),
  computed: {
    readableLimit() {
      const limit = this.sizeLimit / 1024 / 1024;
      return limit.toFixed(1).replace('.0', '') + ' MB';
    },
    readableFileTypes() {
      const filetypes = this.accept.split(',')
        .map(type => type.trim())
        .map(type => type.replace('image/', '').toLowerCase())
        .map(type => type.split('+')[0])
        .map(type => type === 'jpeg' ? 'jpg' : type);

      if (filetypes.length === 1) {
        return filetypes[0];
      }
      if (filetypes.length === 2) {
        return filetypes.join(' ' + this.tr('or') + ' ');
      }
      return filetypes.slice(0, -1).join(', ') + ' ' + this.tr('or') + ' ' + filetypes[filetypes.length - 1];
    },
    active() {
      return this.imagePreview !== null;
    },
    buttonLabel() {
      if (!this.active) {
        return this.label;
      }

      return this.activeLabel ?? this.label;
    },
  },
  watch: {
    modelValue(newFile) {
      if (newFile) {
        this.imagePreview = URL.createObjectURL(newFile);
        this.uploadedFileName = newFile.name;
      }
      else {
        this.imagePreview = null;
        this.uploadedFileName = null;
      }
    },
  },
  methods: {
    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) {
        this.$emit('update:modelValue', null);
        return;
      }

      if (file.size > this.sizeLimit) {
        this.error(this.tr('Image size is too big. Maximum size is :size.', { ':size': this.readableLimit }));
        this.$refs.fileInput.value = null;
        return;
      }

      this.$emit('update:modelValue', file);
    },
    onClickButton() {
      if (this.active) {
        this.removeImage();
        return;
      }

      this.$refs.fileInput.click();
    },
    removeImage() {
      this.$emit('update:modelValue', null);
      this.$refs.fileInput.value = null;
    },
  },
};
</script>
